import { VulcanHook, VulcanThis } from '../app';
import { isMobile } from '../utils/ismobile';
import throttle from 'lodash/throttle';

type Globals = {
  elements: {
    title: HTMLElement | null;
    header: HTMLElement | null;
    footer: Element | null;
    filter_categories: HTMLElement | null;
    filters: HTMLElement | null;
  };
  css: {
    titleHeight: number;
    titlePaddingTop: string;
    headerHeight: number;
    footerHeight: number;
  };
  scrollToggleNav: boolean;
  scrollOffset: number;
  shouldScrollAfterUpdated: boolean;
};
type Callbacks = {
  setupElements: () => void;
  setupCSS: () => void;
  updateCSS: () => void;
  scrollFilters: () => void;
  addEventListeners: () => void;
};
type Hook = VulcanHook & Callbacks;
type This = VulcanThis & Callbacks & Globals;

// Convert a number into a pixel format.
const toPixel = (n: number) => `${n}px`;

// The default pixel value.
const defaultPixel = () => `0px`;

// Determines the max height according with the provided height.
const maxHeight = (height: string) => `calc(100dvh - ${height})`;

export const filters: Hook = {
  mounted(this: This) {
    const scrollOffsetAttribute = this.el.getAttribute('vulcan-postfilter-scroll-offset');
    this.scrollOffset = scrollOffsetAttribute ? parseInt(scrollOffsetAttribute) : 0;
    this.shouldScrollAfterUpdated = this.el.hasAttribute('vulcan-postfilter-scroll');
    this.scrollToggleNav = this.el.hasAttribute('vulcan-scroll-toggle-nav');

    this.setupElements();
    this.setupCSS();
    this.updateCSS();
    this.scrollFilters = throttle(this.scrollFilters, 100).bind(this);
    this.addEventListeners();
  },
  updated(this: This) {
    this.updateCSS();
    if (this.shouldScrollAfterUpdated) {
      const y = this.el.parentElement.getBoundingClientRect().top + window.scrollY;
      window.scroll({
        top: y + this.scrollOffset
      });
    }
  },
  setupElements(this: This) {
    const title = this.el.querySelector('[vulcan-id=title]') as HTMLElement | null;
    const header = document.querySelector('[vulcan-id=header]') as HTMLElement | null;
    const footer = document.querySelector('[vulcan-id=footer]');
    const filter_categories = document.querySelector(
      '[vulcan-id=filter-categories]'
    ) as HTMLElement | null;
    const filters = this.el.querySelector('[vulcan-id=filters]') as HTMLElement | null;

    this.elements = { title, header, footer, filter_categories, filters };
  },
  setupCSS(this: This) {
    const titleHeight = this.elements.title?.getBoundingClientRect().height || 0;
    const headerHeight = this.elements.header?.getBoundingClientRect().height || 0;
    const footerHeight = this.elements.footer?.getBoundingClientRect().height || 0;

    // Clean-up the title before get the padding top from computed style.
    if (this.elements.title) {
      this.elements.title.style.paddingTop = '';
    }

    this.css = {
      titleHeight,
      titlePaddingTop: this.elements.title
        ? window.getComputedStyle(this.elements.title, null).getPropertyValue('padding-top')
        : defaultPixel(),
      headerHeight,
      footerHeight
    };
  },
  // Updates top, maxHeight.
  updateCSS(this: This) {
    // If there's no title or filter categories or filters elements, abort.
    if (
      !(
        this.elements.filter_categories &&
        this.elements.filters &&
        this.elements.title &&
        this.elements.header
      )
    ) {
      return;
    }

    // If a mobile user trigger a filter, cleanup the styles.
    if (isMobile()) {
      this.elements.filters.style.top = '';
      this.elements.title.style.paddingTop = '';
      this.elements.filter_categories.style.maxHeight = '';

      return;
    }

    // Distance between the title element and the top of the page.
    const topDistance = this.elements.title.getBoundingClientRect().top;

    const height = toPixel(topDistance + this.css.titleHeight + this.css.footerHeight);

    this.elements.filter_categories.style.maxHeight = maxHeight(height);

    if (this.scrollToggleNav) {
      const top = parseInt(this.elements.header.style.top);

      if (topDistance === 0 && top <= 0) {
        const paddingTop = top + this.css.headerHeight + parseInt(this.css.titlePaddingTop);
        const height = toPixel(paddingTop + this.css.titleHeight + this.css.footerHeight);

        this.elements.title.style.paddingTop = toPixel(paddingTop);
        this.elements.filter_categories.style.maxHeight = maxHeight(height);
      }

      return;
    }
    // When the nav doesn't hide/show we can safely use the 'top' property.
    this.elements.filters.style.top = toPixel(this.css.headerHeight);
  },
  // Updates paddingTop, maxHeight.
  scrollFilters(this: This) {
    // If there's no title or filter categories or filters or header elements , abort.
    if (
      !(
        this.elements.header &&
        this.elements.title &&
        this.elements.filter_categories &&
        this.elements.filters
      )
    ) {
      return;
    }

    // If a mobile user trigger a filter, cleanup the styles.
    if (isMobile()) {
      this.elements.filters.style.top = '';
      this.elements.title.style.paddingTop = '';
      this.elements.filter_categories.style.maxHeight = '';

      return;
    }

    // Distance between the title element and the top of the page.
    const topDistance = this.elements.title.getBoundingClientRect().top;

    const height = toPixel(topDistance + this.css.titleHeight + this.css.footerHeight);

    this.elements.filter_categories.style.maxHeight = maxHeight(height);

    // We only want add paddingTop to the title element when we're handling a
    // scroll toggle nav, otherwise the nav will always be visible and we can safely
    // use the top property with headerHeight.
    if (this.scrollToggleNav) {
      const top = parseInt(this.elements.header.style.top);

      // When the user scrolled to the product grid (topDistance === 0)
      // and the header is appearing (top <= 0) we will calculate the header height according with its top property.
      if (topDistance === 0 && top <= 0) {
        const paddingTop = top + this.css.headerHeight + parseInt(this.css.titlePaddingTop);
        const height = toPixel(paddingTop + this.css.titleHeight + this.css.footerHeight);
        this.elements.title.style.paddingTop = toPixel(paddingTop);
        this.elements.filter_categories.style.maxHeight = maxHeight(height);

        return;
      }
      // We only want add a padding top when topDistance is smaller than the headerHeight
      // and the header is visible (top === 0)
      // and the header is actually at the top of the page (window.scrollY < this.css.headerHeight)
      const paddingTop =
        topDistance < this.css.headerHeight && top === 0 && window.scrollY < this.css.headerHeight
          ? toPixel(this.css.headerHeight + parseInt(this.css.titlePaddingTop) - topDistance)
          : '';

      this.elements.title.style.paddingTop = paddingTop;
      this.elements.filter_categories.style.maxHeight = maxHeight(height);
    }
  },
  addEventListeners(this: This) {
    window.addEventListener('resize', this.setupCSS.bind(this));
    window.addEventListener('scroll', this.scrollFilters.bind(this));
  }
};
